import { privacy } from "@redotech/schema-privacy";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  ArrayStringFilterBuilder,
  ArrayStringFilterDataSchema,
} from "./array-string-filter";
import {
  BooleanFilterBuilder,
  BooleanFilterDataSchema,
} from "./boolean-filter";
import { DateFilterBuilder, DateFilterDataSchema } from "./date-filter";
import { ExistsFilterBuilder, ExistsFilterDataSchema } from "./exists-filter";
import {
  AdvancedFilterType,
  FilterOperator,
  GenericFilterBuilder,
  GenericFilterClause,
  GenericFilterGroup,
} from "./generic-advanced-filter-data";
import { NumberFilterBuilder, NumberFilterDataSchema } from "./number-filter";
import { StringFilterBuilder, StringFilterDataSchema } from "./string-filter";

const AdvancedFilterDataSchema_ = z.discriminatedUnion("type", [
  DateFilterDataSchema.meta({ privacy: privacy.structured }),
  ArrayStringFilterDataSchema.meta({ privacy: privacy.structured }),
  NumberFilterDataSchema.meta({ privacy: privacy.structured }),
  StringFilterDataSchema.meta({ privacy: privacy.structured }),
  ExistsFilterDataSchema.meta({ privacy: privacy.structured }),
  BooleanFilterDataSchema.meta({ privacy: privacy.structured }),
]);
export const AdvancedFilterDataSchema: z.ZodType<AdvancedFilterData> =
  AdvancedFilterDataSchema_.meta({ privacy: privacy.structured });
export type AdvancedFilterData = z.infer<typeof AdvancedFilterDataSchema_>;

// Concrete filter types using the generic definitions
export type FilterClause = GenericFilterClause<AdvancedFilterData>;
export type FilterGroup = GenericFilterGroup<AdvancedFilterData>;

const FilterGroupSchema_: z.ZodType<FilterGroup> = z.lazy(() =>
  z
    .object({
      operator: createEnum(FilterOperator, { enumName: "FilterOperator" }).meta(
        { privacy: privacy.nonPii },
      ),
      clauses: z
        .array(FilterClauseSchema)
        .meta({ privacy: privacy.structured }),
    })
    .meta({ privacy: privacy.structured }),
);
export const FilterGroupSchema: z.ZodType<FilterGroup> = FilterGroupSchema_;

const FilterClauseSchema_: z.ZodType<FilterClause> = z.lazy(() =>
  z
    .union([AdvancedFilterDataSchema, FilterGroupSchema])
    .meta({ privacy: privacy.structured }),
);
export const FilterClauseSchema: z.ZodType<FilterClause> = FilterClauseSchema_;

export function getFilterBuilder(
  filter: AdvancedFilterData | undefined,
): GenericFilterBuilder<any, any, any> {
  if (!filter) {
    throw new Error("filter is undefined");
  }

  switch (filter.type) {
    case AdvancedFilterType.DATE:
      return DateFilterBuilder;
    case AdvancedFilterType.ARRAY_TO_STRING:
      return ArrayStringFilterBuilder;
    case AdvancedFilterType.NUMBER:
      return NumberFilterBuilder;
    case AdvancedFilterType.STRING:
      return StringFilterBuilder;
    case AdvancedFilterType.EXISTS:
      return ExistsFilterBuilder;
    case AdvancedFilterType.BOOLEAN:
      return BooleanFilterBuilder;
    default:
      assertNever(filter);
  }
}

export const ADVANCED_FILTER_SEPARATOR = ";;";

/**
 * The `advancedFilters` URL parameter a table reads: one `name@@value` entry
 * per filter, joined with `;;`.
 */
export function encodeAdvancedFilterData(
  filters: AdvancedFilterData[],
): string {
  return filters
    .map((filter) => {
      const encoded = getFilterBuilder(filter).writeToString(filter);
      return `${filter.name}@@${encoded}`;
    })
    .join(ADVANCED_FILTER_SEPARATOR);
}

/** Whether an `advancedFilters` URL parameter applies this exact filter. */
export function advancedFiltersParamHas(
  param: string | null,
  filter: AdvancedFilterData,
): boolean {
  return (
    !!param &&
    param
      .split(ADVANCED_FILTER_SEPARATOR)
      .includes(encodeAdvancedFilterData([filter]))
  );
}

export function filtersAreEqual(
  filter1: AdvancedFilterData,
  filter2: AdvancedFilterData,
): boolean {
  if (filter1.type !== filter2.type) {
    return false;
  }
  if (filter1.name !== filter2.name) {
    return false;
  }

  const filterBuilder = getFilterBuilder(filter1);
  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    filterBuilder.writeToString(filter1 as any) ===
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    filterBuilder.writeToString(filter2 as any)
  );
}

export function advancedFiltersAreEqual(
  filters1: AdvancedFilterData[] | undefined,
  filters2: AdvancedFilterData[] | undefined,
): boolean {
  if (!filters1 && !filters2) {
    return true;
  }
  if (!filters1 || !filters2) {
    return false;
  }
  if (filters1.length !== filters2.length) {
    return false;
  }

  for (const [i, element] of filters1.entries()) {
    const other = filters2[i];
    if (other === undefined || !filtersAreEqual(element, other)) {
      return false;
    }
  }

  return true;
}
